import { Injectable } from '@angular/core';

import { environment } from '../environment';
import { IChildRecord, ILayerColumn, IOperatorOption, IQuery, IQueryFind, ITable, IWorkingQuery } from '../types';

/* tslint:disable:max-file-line-count */
@Injectable({
    providedIn: 'root'
})
export class ToSqlStringService {
    private readonly multiValueOperators = ['BETWEEN', 'NOT BETWEEN', 'IN', 'NOT IN'];
    private readonly numericDataTypes = ['NUMBER', 'FLOAT'];
    private layerColumnDataTypes: IColumnDataType[] = [];
    private readonly maxOracleItems = 1000;
    private readonly operatorOptions: IOperatorOption[] = [
        { operator: '=', displayText: 'equal to' },
        { operator: '<>', displayText: 'not equal to' },
        { operator: '>', displayText: 'greater than' },
        { operator: '>=', displayText: 'greater than or equal to' },
        { operator: '<', displayText: 'less than' },
        { operator: '<=', displayText: 'less than or equal to' },
        { operator: 'BETWEEN', displayText: 'between' },
        { operator: 'NOT BETWEEN', displayText: 'not between' },
        { operator: 'LIKE', displayText: 'like' },
        { operator: 'NOT LIKE', displayText: 'not like' },
        { operator: 'IN', displayText: 'in' },
        { operator: 'NOT IN', displayText: 'not in' },
        { operator: 'INCLUDES', displayText: 'includes' },
        { operator: 'NOT INCLUDES', displayText: 'does not include' },
        { operator: 'IS NULL', displayText: 'null' },
        { operator: 'IS NOT NULL', displayText: 'not null' },
        { operator: 'GUID', displayText: 'GUID' }
    ];

    /**
     * Recursive method which returns a SQL filter Condition given a Query Object and all of its descendents.
     * @param query takes filter.rootQuery
     */
    public getSql = (query: IQuery, layerColumns: ILayerColumn[] = null, table: ITable = null, prefix = '') => {
        if (layerColumns && layerColumns.length > 0) {
            this.layerColumnDataTypes = layerColumns.map(x => this.getKeyValuePair(x.name, x.databaseDataType));
        }
        let sql = '';
        if (query) {
            if (query.subqueries && query.subqueries.length > 0) {
                const subqueries = this.getValidSubqueries(query.subqueries);
                const conditions: string[] = [];
                for (const subquery of subqueries) {
                    let subquerySql = this.getSql(subquery, [], null, prefix);
                    if (subquerySql && query.operator === 'AND' && subquery.operator === 'OR'
                        && this.getValidSubqueries(subquery.subqueries).length > 1) {
                        subquerySql = `(${subquerySql})`;
                    }
                    if (subquerySql) {
                        conditions.push(subquerySql);
                    }
                }
                sql += conditions.join(` ${query.operator} `);
            } else {
                if (this.queryConditionIsValid(query)) {
                    sql += this.getConditionSql(query, prefix);
                }
            }
        }
        const parts = [];
        if (table) {
            const fromClause = `SELECT * FROM ${table.owner.toLowerCase()}.${table.name.toLowerCase()} ${prefix}`;
            parts.push(fromClause);
        }
        if (sql) {
            parts.push(sql);
        }
        sql = parts.join(' WHERE ');
        return sql;
    }

    public getChildWhereClause(childTableQuery: IQuery, childPrefix: string) {
        let sql = '';
        if (childTableQuery && childTableQuery.subqueries && childTableQuery.subqueries.length > 0) {
            const subqueries = this.getValidSubqueries(childTableQuery.subqueries);
            const conditions: string[] = [];
            for (const subquery of subqueries) {
                let subquerySql = this.getSql(subquery, [], null, childPrefix);
                if (subquerySql && childTableQuery.operator === 'AND' && subquery.operator === 'OR'
                    && this.getValidSubqueries(subquery.subqueries).length > 1) {
                    subquerySql = `(${subquerySql})`;
                }
                if (subquerySql) {
                    conditions.push(subquerySql);
                }
            }
            const finalCondition = conditions.join(` ${childTableQuery.operator} `);
            sql += conditions.length > 1 ? `(${finalCondition})` : finalCondition;
        } else {
            if (this.queryConditionIsValid(childTableQuery)) {
                sql += this.getConditionSql(childTableQuery, childPrefix);
            }
        }
        return sql;
    }

    public getChildTableFilter(childTableRecord: IChildRecord, prefix: string, childPrefix: string) {
        if (childTableRecord) {
            const childKeyColumnNames = childTableRecord.childParentKeyColumnName.split('||');
            const parentKeyColumnNames = childTableRecord.parentPrimaryKeyColumnName.split('||');
            const clauses: string[] = [];
            for (let i = 0; i < childKeyColumnNames.length; i++) {
                let clause = '';
                if (childKeyColumnNames[i].toUpperCase().includes('DECODE')) {
                    clause = `(TO_CHAR(${childKeyColumnNames[i]}) = TO_CHAR(${parentKeyColumnNames[i]}))`;
                } else {
                    clause = `(TO_CHAR(${(!childKeyColumnNames[i].includes('\'') ? (childPrefix + '.') : '')}${childKeyColumnNames[i]}) = TO_CHAR(${!parentKeyColumnNames[i].includes('\'') ? prefix + '.' : ''}${parentKeyColumnNames[i]}))`;
                }
                clauses.push(clause);
            }
            return ` INNER JOIN ${childTableRecord.childOwner}.${childTableRecord.childTableName} ${childPrefix} ON ${clauses.join(' AND ')} `;
        }
        return '';
    }

    public getJoinWhereClause(joinTableQuery: IQuery, joinPrefix: string) {
        let sql = '';
        if (joinTableQuery && joinTableQuery.subqueries && joinTableQuery.subqueries.length > 0) {
            const subqueries = this.getValidSubqueries(joinTableQuery.subqueries);
            const conditions: string[] = [];
            for (const subquery of subqueries) {
                let subquerySql = this.getSql(subquery, [], null, joinPrefix);
                if (subquerySql && joinTableQuery.operator === 'AND' && subquery.operator === 'OR'
                    && this.getValidSubqueries(subquery.subqueries).length > 1) {
                    subquerySql = `(${subquerySql})`;
                }
                if (subquerySql) {
                    conditions.push(subquerySql);
                }
            }
            const finalCondition = conditions.join(` ${joinTableQuery.operator} `);
            sql += conditions.length > 1 ? `(${finalCondition})` : finalCondition;
        } else {
            if (this.queryConditionIsValid(joinTableQuery)) {
                sql += this.getConditionSql(joinTableQuery, joinPrefix);
            }
        }
        return sql;
    }
    public getJoinFilter(query: IQuery, prefix: string, joinPrefix: string) {
        let sql = '';
        if (query && query.joinRecord && query.joinRecord.isOffsetNumber !== undefined) {
            const baseTableOffsetMark = query.primaryLayerRMSKeys.isOffsetNumber ? '' : "'";
            const joinTableOffsetMark = query.joinRecord && query.joinRecord.isOffsetNumber ? '' : "'";
            const containsPointType = query.primaryLayerRMSKeys.geometryType.toLowerCase().indexOf('points') > -1;
            sql += ` INNER JOIN ${query.joinRecord.tableName.split(',')[0]} ${joinPrefix} ON (${joinPrefix}.${query.joinRecord.ctyCodeColName} = ${prefix}.${query.primaryLayerRMSKeys.ctyCodeColName} AND ${joinPrefix}.${query.joinRecord.strtnoColName} = ${prefix}.${query.primaryLayerRMSKeys.strtnoColName}`;

            //add JURIS if both base table as well as roadway table has the JURIS columns
            if (query.joinRecord.jurisColName !== '' && query.joinRecord.jurisColName !== null && query.joinRecord.jurisColName !== undefined && query.primaryLayerRMSKeys.jurisColName !== '' && query.primaryLayerRMSKeys.jurisColName !== null && query.primaryLayerRMSKeys.jurisColName !== undefined && query.primaryLayerRMSKeys.jurisColName !== 'NO_JURIS!' && query.joinRecord.jurisColName !== 'NO_JURIS!') {
                sql = `${sql} AND NVL(${joinPrefix}.${query.joinRecord.jurisColName}, 'NULL')=NVL(${prefix}.${query.primaryLayerRMSKeys.jurisColName}, 'NULL') `;
            }

            if (query.joinRecord.sideIndColName !== '' && query.joinRecord.sideIndColName !== null && query.joinRecord.sideIndColName !== undefined && query.primaryLayerRMSKeys.sideIndColName !== '' && query.primaryLayerRMSKeys.sideIndColName !== null && query.primaryLayerRMSKeys.sideIndColName !== undefined) {
                sql = `${sql} AND ${joinPrefix}.${query.joinRecord.sideIndColName}=${prefix}.${query.primaryLayerRMSKeys.sideIndColName} `;
            }

            const rmsJoin = this.getRMSJoin(query, prefix, baseTableOffsetMark, joinPrefix, joinTableOffsetMark, containsPointType);
            sql = `${sql + rmsJoin}) `;
        }
        return sql;
    }

    private getRMSJoin(query: IQuery, prefix: string, baseTableOffsetMark: string, joinPrefix: string, joinTableOffsetMark: string, containsPointType: boolean) {
        let a = '', b = '', c = '', d = '', e = '', f = '', g = '', h = '', rmsRow = 0, rmsCol = 0, rmsType = 0, rmsJoin = '';

        if (query.primaryLayerRMSKeys.segBgnColName) {
            a = `${prefix}.${query.primaryLayerRMSKeys.segBgnColName}`;
        }

        if (query.primaryLayerRMSKeys.offsetBgnColName) {
            b = `${prefix}.${query.primaryLayerRMSKeys.offsetBgnColName}`;
        }

        if (query.primaryLayerRMSKeys.segEndColName) {
            c = `NVL(${prefix}.${query.primaryLayerRMSKeys.segEndColName}, ${prefix}.${query.primaryLayerRMSKeys.segBgnColName})`;
        }

        if (query.primaryLayerRMSKeys.offsetEndColName) {
            d = `NVL((case ${prefix}.${query.primaryLayerRMSKeys.offsetEndColName} WHEN ${baseTableOffsetMark}0${baseTableOffsetMark} THEN ${prefix}.${query.primaryLayerRMSKeys.offsetBgnColName} ELSE ${prefix}.${query.primaryLayerRMSKeys.offsetEndColName} END),${prefix}.${query.primaryLayerRMSKeys.offsetBgnColName})`;
        }

        if (query.joinRecord.segBgnColName) {
            e = `${joinPrefix}.${query.joinRecord.segBgnColName}`;
        }

        if (query.joinRecord.offsetBgnColName) {
            f = `${joinPrefix}.${query.joinRecord.offsetBgnColName}`;
        }

        if (query.joinRecord.segEndColName) {
            g = `NVL(${joinPrefix}.${query.joinRecord.segEndColName}, ${joinPrefix}.${query.joinRecord.segBgnColName})`;
        }

        if (query.joinRecord.offsetEndColName) {
            h = `NVL((CASE ${joinPrefix}.${query.joinRecord.offsetEndColName} WHEN ${joinTableOffsetMark}0${joinTableOffsetMark} THEN ${joinPrefix}.${query.joinRecord.offsetBgnColName} ELSE ${joinPrefix}.${query.joinRecord.offsetEndColName} END),${joinPrefix}.${query.joinRecord.offsetBgnColName})`;
        }
        rmsRow = this.getRMSRow(a, b, c, d, rmsRow);
        rmsCol = this.getRMSCol(e, f, g, h, rmsCol);
        rmsType = rmsRow * rmsCol;
        rmsJoin = this.generateFilterSQL(rmsType, rmsJoin, a, b, c, d, e, f, g, h, containsPointType, query);
        return rmsJoin;
    }

    private generateFilterSQL(rmsType: number, rmsJoin: string, a: string, b: string, c: string, d: string, e: string, f: string, g: string, h: string, containsPointType: boolean, query: IQuery) {
        switch (rmsType) {
            case 16:
            case 32:
            case 48:
            case 17:
            case 18:
                rmsJoin = ` AND ${a} = ${e}`;
                break;
            case 64:
                rmsJoin = ` AND ${e} BETWEEN ${a} AND ${c} `;
                break;
            case 34:
                rmsJoin = ` AND ${a} = ${e} AND ${f} = ${b}`;
                break;
            case 51:
                rmsJoin = ` AND ${e} || LPAD(${f},4,'0') BETWEEN ${a} || LPAD(${b},4,'0') AND ${a} || LPAD(${d},4,'0') `;
                break;
            case 68:
                rmsJoin = ` AND ${e} || LPAD(${f},4,'0') BETWEEN ${a} || LPAD(${b},4,'0') AND ${c} || LPAD(${d},4,'0') `;
                break;
            case 36:
                rmsJoin = ` AND ${a} || LPAD(${b},4,'0') BETWEEN ${e} || LPAD(${f},4,'0') AND ${e} || LPAD(${h},4,'0') `;
                break;
            case 54:
                rmsJoin = ` AND ${a} = ${e} AND ${b} = ${f} AND ${d} = ${h}`;
                break;
            case 72:
                rmsJoin = ` AND (${a} || LPAD(${b},4,'0') BETWEEN ${e} || LPAD(${f},4,'0') AND ${e} || LPAD(${h},4,'0') OR ${c} || LPAD(${d},4,'0') BETWEEN ${e} || LPAD(${f},4,'0') AND ${e} || LPAD(${h},4,'0'))`;
                if (!containsPointType) {
                    rmsJoin = ` AND NOT (${a} || LPAD(${b},4,'0') = ${e} || LPAD(${h},4,'0') OR ${c} || LPAD(${d},4,'0') = ${e} || LPAD(${f},4,'0'))`;
                }
                break;
            case 19:
                let offsetClause = '';
                if ((query.joinRecord.offsetEndColName !== null && query.primaryLayerRMSKeys.segBgnColName === 'SEG_NO')) {
                    offsetClause = `((${query.joinRecord.offsetEndColName} != 0 AND ${a} <= ${g}) OR (${query.joinRecord.offsetEndColName} = 0 AND ${a} < ${g}))`;
                } else if ((query.primaryLayerRMSKeys.offsetEndColName !== null && query.joinRecord.segBgnColName === 'SEG_NO')) {
                    offsetClause = `((${query.primaryLayerRMSKeys.offsetEndColName} != 0 AND ${a} <= ${g}) OR (${query.primaryLayerRMSKeys.offsetEndColName} = 0 AND ${a} < ${g}))`;
                }
                if (containsPointType) {
                    rmsJoin = ` AND ${a} BETWEEN ${e} AND ${g} `;
                } else {
                    if (offsetClause !== '') {
                        rmsJoin = ` AND ${a} >= ${e} AND ${offsetClause}`;
                    } else {
                        rmsJoin = ` AND ${a} >= ${e} AND ${a} <= ${g}`;
                    }

                }
                break;
            case 38:
                rmsJoin = ` AND ${a} || LPAD(${b},4,'0') BETWEEN ${e} || LPAD(${f},4,'0') AND ${g} || LPAD(${h},4,'0') `;
                break;
            case 57:
                rmsJoin = ` AND (${a} || LPAD(${b},4,'0') BETWEEN ${e} || LPAD(${f},4,'0') AND ${g} || LPAD(${h},4,'0') OR ${a} || LPAD(${d},4,'0') BETWEEN ${e} || LPAD(${f},4,'0') AND ${g} || LPAD(${h},4,'0'))`;
                if (!containsPointType) {
                    rmsJoin = ` AND NOT (${a} || LPAD(${b},4,'0') = ${g} || LPAD(${h},4,'0') OR ${a} || LPAD(${d},4,'0') = ${e} || LPAD(${f},4,'0'))`;
                }

                break;
            case 76:
                rmsJoin = ` AND ((${a} || LPAD(${b},4,'0') BETWEEN ${e} || LPAD(${f},4,'0') AND ${g} || LPAD(${h},4,'0') OR ${c} || LPAD(${d},4,'0') BETWEEN ${e} || LPAD(${f},4,'0') AND ${g} || LPAD(${h},4,'0')) OR (${e} || LPAD(${f},4,'0') BETWEEN ${a} || LPAD(${b},4,'0') AND ${c} || LPAD(${d},4,'0') OR ${g} || LPAD(${h},4,'0') BETWEEN ${a} || LPAD(${b},4,'0') AND ${c} || LPAD(${d},4,'0')))`;
                if (!containsPointType) {
                    rmsJoin += ` AND NOT (${a} || LPAD(${b},4,'0') = ${g} || LPAD(${h},4,'0') OR ${c} || LPAD(${d},4,'0') = ${e} || LPAD(${f},4,'0'))`;
                }
                break;
            default:
                break;
        }
        return rmsJoin;
    }

    private getRMSCol(e: string, f: string, g: string, h: string, rmsCol: number) {
        if (e !== '' && f === '' && g === '' && h === '') { rmsCol = 16; }
        if (e !== '' && f !== '' && g === '' && h === '') { rmsCol = 17; }
        if (e !== '' && f !== '' && g === '' && h !== '') { rmsCol = 18; }
        if (e !== '' && f !== '' && g !== '' && h !== '') { rmsCol = 19; }
        return rmsCol;
    }

    private getRMSRow(a: string, b: string, c: string, d: string, rmsRow: number) {
        if (a !== '' && b === '' && c === '' && d === '') { rmsRow = 1; }
        if (a !== '' && b !== '' && c === '' && d === '') { rmsRow = 2; }
        if (a !== '' && b !== '' && c === '' && d !== '') { rmsRow = 3; }
        if (a !== '' && b !== '' && c !== '' && d !== '') { rmsRow = 4; }
        return rmsRow;
    }

    getSQLFromQuery(rootQuery: IQuery, baseTable: ITable, layerColumns: ILayerColumn[]) {
        const baseTableAlias = 'TBL';
        const joinTableAlias = 'JOINTABLE';
        const childTableAlias = 'CHILDTABLE';
        const baseTableSQL = this.getSql(rootQuery, layerColumns, baseTable, baseTableAlias);
        let sql = baseTableSQL;
        for (const query of rootQuery.subqueries) {
            if (query && (query.joinRecord || query.childTableRecord || query.joinTableQuery || query.childTableQuery)) {
                const joinTableSQL = this.getJoinFilter(query, query.joinRecord ? baseTableAlias : '', joinTableAlias); //inner join
                const childTableSQL = this.getChildTableFilter(query.childTableRecord, query.childTableRecord ? baseTableAlias : '', childTableAlias); //inner join
                const joinWhereClause = this.getJoinWhereClause(query.joinTableQuery, joinTableAlias);
                const childWhereClause = this.getChildWhereClause(query.childTableQuery, childTableAlias);
                const baseSQLs = baseTableSQL.toUpperCase().split('WHERE');
                sql = baseSQLs[0] + joinTableSQL + childTableSQL;
                const whereClauses: string[] = [];
                if (joinWhereClause !== '') { whereClauses.push(joinWhereClause); }
                if (childWhereClause !== '') { whereClauses.push(childWhereClause); }
                if (baseSQLs[1] || joinWhereClause !== '' || childWhereClause !== '') {
                    sql += ' WHERE ';
                    if (baseSQLs.length > 1) {
                        whereClauses.push(baseSQLs[1]);
                    }
                    sql += whereClauses.join(' AND ');
                }
            }
        }
        return sql;
    }

    /**
     * Returns the alias given a column name.
     */
    public getAliasFromColumnName(columnName: string, layerColumns: ILayerColumn[]) {
        const foundLayerColumn = layerColumns.filter(x => x.name.toLowerCase() === columnName.toLowerCase());
        if (foundLayerColumn.length > 0) {
            return foundLayerColumn[0].alias || columnName;
        }
        return columnName;
    }

    /**
     * Returns the operator option display text for a given an operator.
     */
    public getOperationOptionDisplayTextFromOperator(operator: string) {
        const foundOperaterOption = this.operatorOptions.filter(x => x.operator.toLowerCase() === operator.toLowerCase());
        if (foundOperaterOption.length > 0) {
            return foundOperaterOption[0].displayText;
        }
        return operator;
    }

    /**
     * Returns true if the query itself, field, operator, and value is populated.
     * If the operator is a NULL check the value is then not required.
     */
    public queryConditionIsValid(query: IQuery): boolean {
        if (!query.isMultiple && query.multiValue && query.value && query.value.includes(',') && query.multiValue.join(',') === query.value) {
            return false;
        }

        return !!query && !!query.field && !!query.operator && (query.value !== null || query.operator.indexOf('NULL') >= 0);
    }

    /**
     * Returns the query objects that are valid from a list.
     */
    public getValidSubqueries(subqueries: IQuery[]) {
        return subqueries.filter(
            x => !!x && (x.operator === 'AND' || x.operator === 'OR' || this.queryConditionIsValid(x))
        );
    }

    /**
     * Returns the operator options given a data type.
     */
    getOperatorOptionsForDatatype(datatype: string): IOperatorOption[] {
        if (!datatype) {
            datatype = '';
        }
        switch (datatype.toLowerCase()) {
            case 'number':
            case 'float':
            case 'date':
                return this.operatorOptions.filter(x => x.operator.indexOf('LIKE') < 0);
                break;
            default:
                return this.operatorOptions;
                break;
        }
    }

    /**
     * Returns the conditions of the query in a readable form.
     */
    public getSummaryItems(query: IQuery, layerColumns: ILayerColumn[]): IQuery[] {
        let summaryItems = [];
        const validSubqueries = this.getValidSubqueries(query.subqueries);
        for (const subquery of validSubqueries) {
            if (subquery.subqueries && subquery.subqueries.length) {
                summaryItems = summaryItems.concat(this.getSummaryItems(subquery, layerColumns));
            } else {
                const summaryItem: IQuery = {};
                summaryItem.field = this.getAliasFromColumnName(subquery.field, layerColumns);
                summaryItem.operator = this.getOperationOptionDisplayTextFromOperator(subquery.operator);
                if (summaryItem.operator.indexOf('NULL') < 0) {
                    summaryItem.value = subquery.value;
                }
                summaryItems.push(summaryItem);
            }
        }
        return summaryItems;
    }

    public getSummaryStringForQuery(query: IQuery, layerColumns: ILayerColumn[]): string {
        let summaryString = '';
        query = this.getSimplifiedDeepCopyOfQuery(query);
        summaryString += this.parseIQueryToString(query, layerColumns);
        if (summaryString.length > 0) {
            summaryString = `when ${summaryString}`;
        }
        return summaryString;
    }

    public getJoinFilterSummaryFromQuery(workingQuery: IQuery, joinColumns: ILayerColumn[]) {
        let summaryItemsString = '';
        let joinItemString = '';
        if (workingQuery.joinRecord) {

            summaryItemsString = `joined to ${workingQuery.joinRecord.layerDisplayName}`;
        }
        if (workingQuery.joinTableQuery && joinColumns) {
            joinItemString = this.parseIQueryToString(workingQuery.joinTableQuery, joinColumns);
            if (joinItemString !== '') {
                summaryItemsString = `${summaryItemsString} where ${joinItemString}`;
            }
        }
        return summaryItemsString;
    }

    public getChildFilterSummaryFromQuery(workingQuery: IQuery, childColumns: ILayerColumn[]) {
        let summaryItemsString = '';
        let childItemString = '';
        if (workingQuery.childTableRecord) {
            if (summaryItemsString !== '') {
                summaryItemsString += ' and ';
            }
            summaryItemsString = `has a child record in ${workingQuery.childTableRecord.layerChildRelationshipName}`;
        }
        if (workingQuery.childTableQuery && childColumns) {
            childItemString = this.parseIQueryToString(workingQuery.childTableQuery, childColumns);
            if (childItemString !== '') {
                summaryItemsString = `${summaryItemsString} where ${childItemString}`;
            }
        }
        return summaryItemsString;
    }

    public parseIQueryToString(query: IQuery, layerColumns: ILayerColumn[]): string {
        let summaryString = '';
        if (query.field) {
            summaryString += this.getSummaryString(query, layerColumns);
        } else if (query.subqueries) {
            const validSubqueries = this.getValidSubqueries(query.subqueries);
            for (const subquery of validSubqueries) {
                if (summaryString.length > 0) {
                    summaryString += query.operator === 'OR' ? ' or ' : ' and ';
                }
                summaryString += this.parseIQueryToString(subquery, layerColumns);
            }
        }
        return summaryString;
    }

    public getSummaryString(query: IQuery, layerColumns: ILayerColumn[]): string {
        const summaryItem: IQuery = {};
        summaryItem.field = this.getAliasFromColumnName(query.field, layerColumns);
        summaryItem.operator = this.getOperationOptionDisplayTextFromOperator(query.operator);
        if (summaryItem.operator.indexOf('NULL') < 0) {
            summaryItem.value = query.value;
        }
        if (summaryItem.operator.toLowerCase() === 'guid') {
            summaryItem.field += "'s";
            summaryItem.operator += ' is ';
        } else if (summaryItem.operator.toLowerCase().indexOf('includes') < 0) {
            summaryItem.field += ' is ';
        }
        if (summaryItem.operator.toLowerCase().indexOf('between') >= 0) {
            summaryItem.value = summaryItem.value.replace(',', ' AND ');
        }
        return [summaryItem.field, summaryItem.operator, summaryItem.value].join(' ');
    }

    private readonly getKeyValuePair = (alias: string, dataType: string) => {
        const pair: IColumnDataType = { key: alias, value: dataType };
        return pair;
    }

    private readonly getConditionSql = (query: IQuery, prefix = '') => {
        const prefixAddition = prefix.length > 0 ? `${prefix}.` : '';
        if (query.value === undefined || query.value === null) {
            query.value = '';
        }
        let columnName: string = query.field.toLowerCase();
        if (columnName === 'has_geometry') {
            columnName = `NVL2(${prefixAddition}GEOMETRY,'Y','N')`;
        }
        if (query.operator !== 'GUID') {
            this.validateValue(query);
        }
        const includeSingleQuote = !this.isQueryFieldNumeric(query);
        const includeUpper = this.includeUpper(query);
        if (query.operator.indexOf('BETWEEN') >= 0) {
            const isDate = this.getQueryDataType(query) === 'DATE';
            return this.getBetweenCondition(columnName, query.value.split(','), query.operator, includeSingleQuote, isDate, includeUpper, this.getQueryDataType(query));
        } else if (this.getQueryDataType(query) === 'DATE') {
            return this.getDateCondition(columnName, query.operator, query.value, query.multiValue);
        } else if (query.operator === 'INCLUDES' || query.operator === 'NOT INCLUDES') {
            return this.getIncludesFullValue(query, includeUpper);
        } else if (this.multiValueOperators.includes(query.operator)) {
            if (query.isMultiple && query.multiValue) {
                return this.getMultivalueCondition(columnName, query.multiValue, includeSingleQuote, query.operator, includeUpper, query.separatorCharacter, this.getQueryDataType(query));
            } else {
                return this.getMultivalueCondition(columnName, query.value.split(','), includeSingleQuote, query.operator);
            }
        } else if (query.operator === 'GUID') {
            return this.getMultivalueCondition(columnName, query.value.split(','), includeSingleQuote, query.operator);
        } else {
            let value = query.value;
            if (value != null) {
                if (query.operator.indexOf('LIKE') >= 0) {
                    if (value.indexOf('%') < 0) {
                        value = `%${query.value}%`;
                    }
                }
            }
            if (this.getQueryDataType(query) === 'CLOB') {
                let operator;
                if (query.operator === '=') {
                    operator = 'LIKE';
                } else if (query.operator === '<>') {
                    operator = 'NOT LIKE';
                } else {
                    return this.getCondition(columnName, query.operator, value, includeSingleQuote, includeUpper, false, this.getQueryDataType(query));
                }
                return this.getCondition(columnName, operator, value, includeSingleQuote, includeUpper);
            } else {
                return this.getCondition(columnName, query.operator, value, includeSingleQuote, includeUpper);
            }
        }
    }

    public getOperatorOptions() {
        return this.operatorOptions;
    }

    public cloneSubqueries(sourceQuery: IQuery, destinationQuery: IQuery) {
        destinationQuery.subqueries = [];
        for (const subquery of sourceQuery.subqueries) {
            destinationQuery.subqueries.push(this.getSimplifiedDeepCopyOfQuery(subquery));
        }
    }

    public getShallowCopyOfQuery(query: IQuery, lowerCase: boolean = true) {
        if (query.field) {
            const newQuery: IQuery = {
                field: lowerCase ? query.field.toLowerCase() : query.field,
                operator: query.operator,
                value: query.value
            };
            if (query.name) {
                newQuery.name = query.name;
            }
            if (query.isMultiple) {
                newQuery.isMultiple = query.isMultiple;
                newQuery.multiValue = query.multiValue;
            }
            if (query.separatorCharacter) {
                newQuery.separatorCharacter = query.separatorCharacter;
            }
            return newQuery;
        } else {
            return {};
        }
    }

    public getSimplifiedDeepCopyOfQuery(query: IQuery, lowerCase: boolean = true): IQuery {
        const newQuery: IQuery = {};
        if (!query) {
            return newQuery;
        } else if (query.subqueries && query.subqueries.length > 0) {
            newQuery.operator = query.operator;
            const validSubqueries = this.getValidSubqueries(query.subqueries);
            if (validSubqueries.length === 0) {
                return newQuery;
            } else if (validSubqueries.length === 1) {
                return this.getSimplifiedDeepCopyOfQuery(validSubqueries[0], lowerCase);
            } else {
                newQuery.operator = query.operator;
                newQuery.subqueries = [];
                for (const subquery of validSubqueries) {
                    let newSubquery: IQuery = {};
                    if (subquery.isHiddenFilter) {
                        newSubquery = {
                            operator: subquery.operator,
                            isHiddenFilter: subquery.isHiddenFilter,
                            subqueries: subquery.subqueries
                        };
                    } else {
                        newSubquery = this.getSimplifiedDeepCopyOfQuery(subquery, lowerCase);
                    }
                    newQuery.subqueries.push(newSubquery);
                }
                return newQuery;
            }
        } else {
            return this.getShallowCopyOfQuery(query, lowerCase);
        }
    }

    public addFilter(layerQuery: IQuery, newFilter: IQuery) {
        let query: IQuery;
        if (layerQuery.field !== undefined) {
            const copiedQuery = this.getSimplifiedDeepCopyOfQuery(layerQuery);
            query = {};
            query.operator = 'AND';
            query.subqueries = [];
            if (copiedQuery) {
                query.subqueries.push(copiedQuery);
            }
            if (newFilter) {
                query.subqueries.push(newFilter);
            }
            delete query.value;
            delete query.field;
        } else if (layerQuery.subqueries !== undefined && layerQuery.subqueries.length >= 1) {
            const copiedQuery = this.getSimplifiedDeepCopyOfQuery(layerQuery);
            if (copiedQuery.operator === 'AND') {
                query = copiedQuery;
                if (query.subqueries.length) { // Temp fix for situation where last clause was damaged
                    const lastClause = query.subqueries[query.subqueries.length - 1];
                    if (!this.queryConditionIsValid(lastClause) && !lastClause.subqueries) {
                        query.subqueries.pop();
                    }
                }
                query.subqueries.push(newFilter);
            } else {
                query = { operator: 'AND', subqueries: [copiedQuery] };
                query.subqueries.push(newFilter);
            }
        }
        //no filter available set the new filter.
        if ((Object.keys(layerQuery).length === 0 || (!layerQuery.field && !(layerQuery.subqueries && layerQuery.subqueries.length > 0))) && newFilter) {
            query = { ...newFilter };
        }
        // Child Table Query filter setting
        query = this.getChildTableQuery(layerQuery, query);
        // Join Table Query filter setting
        query = this.getJoinTableQuery(layerQuery, query);

        if (Object.keys(layerQuery).length === 0 && !newFilter) {
            query = null;
        }
        if (!query) {
            query = {};
            query.operator = 'AND';
            query.subqueries = [];
        }
        return query;
    }

    public replaceGUID(layerQuery: IQuery, existingGuid: string, guid: string) {
        if (layerQuery.subqueries != undefined && layerQuery.subqueries.length > 0) {
            const selectedQuery = layerQuery.subqueries.filter(q => q.value === existingGuid)[0];
            selectedQuery.value = guid;
        } else if (layerQuery.field !== undefined && layerQuery.value === existingGuid) {
            layerQuery.value = guid;
        }
        return layerQuery;
    }

    public replaceObjQuery(layerQuery: IQuery, existingGuid, guid) {
        if (layerQuery.subqueries != undefined && layerQuery.subqueries.length > 0) {
            const selectedQuery = layerQuery.subqueries.filter(q => q.value === existingGuid)[0];
            selectedQuery.value = guid;
        } else if (layerQuery.field !== undefined && layerQuery.value === existingGuid) {
            layerQuery.value = guid;
        }
        return layerQuery;
    }

    public getCurrentYear() {
        const now = new Date();
        return now.getFullYear();
    }

    public getCurrentFiscalYear() {
        const now = new Date();
        const fiscalYearStartingMonth = 6; // 6 = July, month is zero-based
        if (now.getMonth() >= fiscalYearStartingMonth) {
            return now.getFullYear();
        }
        return now.getFullYear() - 1;
    }

    public getFirstDayOfYear(delimiter = '', date: Date = null) {
        if (!date) {
            date = new Date();
        }
        const year = date.getFullYear().toString();
        return [year, '01', '01'].join(delimiter);
    }

    public addDaysToDate(date: Date, days: number): Date {
        const result = new Date(date);
        result.setDate(<number>result.getDate() + days);
        return result;
    }

    public addMonthsToDate(date: Date, months: number): Date {
        const newDate = date.getDate();
        date.setMonth(<number>date.getMonth() + months);
        if (date.getDate() !== newDate) {
            date.setDate(0);
        }
        return date;
    }

    public addYearsToDate(date: Date, years: number): Date {
        const newYear = <number>date.getFullYear() + years;
        return new Date(newYear, date.getMonth(), date.getDate());
    }

    public getYYYYMMDD(delimiter = '', date: Date = null) {
        if (!date) {
            date = new Date();
        }
        const zeroPadNum = 2;
        const month = (date.getMonth() + 1).toString().padStart(zeroPadNum, '0');
        const day = date.getDate().toString().padStart(zeroPadNum, '0');
        const year = date.getFullYear().toString();
        return [year, month, day].join(delimiter);
    }

    /*
      * Returns a copy of a query with matching clauses removed.
      */
    public getQueryWithoutClauses(query: IQuery, template: IQueryFind | IQueryFind[]): IQuery {
        const root = this.getSimplifiedDeepCopyOfQuery(query);
        return this.removeClauses(root, template);
    }

    /*
     * Removes matching clauses from the query and any children subqueries.
     */
    public removeClauses(query: IQuery, template: IQueryFind | IQueryFind[]): IQuery {
        if (query.subqueries) {
            query.subqueries = query.subqueries.filter(q => this.removeClauses(q, template).operator);
        }
        return (query.subqueries && !query.subqueries.length) || this.clauseMatch(query, template) ? {} : query;
    }

    /**
     * Gets matching clauses from the query and any children subqueries.
     * @param query A mapcore query object
     * @param template A string or RegExp if searching by name field or IQueryFind object for more complex field matching queries
     */
    public getClauses(query: IQuery, template: string | RegExp | IQueryFind | IQueryFind[]): IQuery[] {
        template = (typeof template === 'string' || template instanceof RegExp) ? { name: template } : template;
        if (this.clauseMatch(query, template)) {
            return [query];
        } else if (query.subqueries) {
            return query.subqueries.flatMap(q => this.getClauses(q, template));
        } else {
            return [];
        }
    }

    /*
     * Adds the queryToAdd to the query if it is not already present in the query object
     */
    public addQueryClauseIfNotPresent(query: IQuery, queryToAdd: IQueryFind): IQuery {
        if (queryToAdd) {
            const clauses = this.getClauses(query, queryToAdd);
            if (!clauses.length && queryToAdd.field) {
                const queryCondition = {
                    field: queryToAdd.field.toString(),
                    operator: queryToAdd.operator.toString(),
                    value: queryToAdd.value !== undefined ? queryToAdd.value.toString() : null
                };
                return this.addFilter(query, queryCondition);
            }
        }
        return query;
    }

    /*
     * Returns a simplified query object with the petl project condition if the layer id is the PETL layer and the query contains a project number condition
     */
    public getPetlProjectIdClauseIfPresent(layerId: number, query: IQuery, forcePetl?: boolean): IQuery {
        if (environment.layers.find(x => x.name === 'petl').id === layerId || forcePetl) {
            const projectIdClauses = this.getClauses(query, { field: 'project_no', operator: '=' });
            if (projectIdClauses.length) {
                return this.getSimplifiedQueryClause(projectIdClauses[0]);
            }
        }
        return null;
    }

    /**
     * Return a simplified query object with just the field, operator, and value.
     */
    public getSimplifiedQueryClause(query: IQuery): IQuery {
        return { field: query.field, operator: query.operator, value: query.value };
    }

    /*
     * Checks if a query clause matches passed in templates. Returning matching templates or null when no matches are found.
     */
    private clauseMatch(clause: IQuery, template: IQueryFind | IQueryFind[]): IQueryFind[] {
        const templates = Array.isArray(template) ? template : [template];
        const matched = templates.filter(t =>
            (!t.field || this.stringMatch(clause.field, t.field)) &&
            (!t.name || this.stringMatch(clause.name, t.name)) &&
            (!t.operator || this.stringMatch(clause.operator, t.operator)) &&
            (!t.value || this.stringMatch(clause.value, t.value))
        );
        return matched.length ? matched : null;
    }

    /*
     * Returns RegExpMatchArray if string matches or undefined if not.
     */
    private stringMatch(stringToMatch: string, expression: string | RegExp): RegExpMatchArray {
        // For the simple string expression, first escape regex special characters. Then allow * and ? to be wildcards
        // by converting their escaped values to the equivalent regex expressions '.*' and '.' respectively.
        const regex = (typeof expression === 'string') ? new RegExp(`^${this.escapeRegExp(expression).replace(/\\\*/gi, '.*').replace(/\\\?/gi, '.')}$`, 'i') : expression;
        return stringToMatch && stringToMatch.match(regex);
    }

    private escapeRegExp(stringToescapeRegExp) {
        return stringToescapeRegExp.replace(/[.*+?^${}()|[\]\\]/gi, '\\$&');
    }

    private getQueryDataType(query: IQuery) {
        if (query.field) {
            const dataKeyIndex = this.layerColumnDataTypes ? this.layerColumnDataTypes.findIndex(x => x.key.toLowerCase() === query.field.toLowerCase()) : -1;
            if (dataKeyIndex >= 0) {
                return this.layerColumnDataTypes[dataKeyIndex].value;
            }
        }
        return null;
    }

    private readonly isQueryFieldNumeric = (query: IQuery) => {
        const queryDataType = this.getQueryDataType(query);
        return queryDataType !== null && this.numericDataTypes.includes(queryDataType);
    }

    private readonly validateValue = (query: IQuery) => {
        const valid = !query.value || !this.isQueryFieldNumeric(query) || this.areValuesNumeric(query) || query.value.indexOf('{') === 0 || query.value === 'NULL';
        if (!valid) {
            throw new Error(`${query.field} is a numeric type but '${query.value}' is not numeric.`);
        }
    }

    private readonly getIncludesFullValue = (query: IQuery, includeUpper) => {
        const not = query.operator === 'NOT INCLUDES';
        let valuesArray;
        if (query.multiValue.length > 0) {
            valuesArray = query.multiValue.map(x => this.getIncludesCondition(query.field, x.trim(), not, includeUpper));
        } else {
            valuesArray = query.value.split(',').map(x => this.getIncludesCondition(query.field, x.trim(), not));
        }
        const includesoperator = not ? 'AND' : 'OR';
        return valuesArray.join(` ${includesoperator} `);
    }

    /**
     * Since Oracle has a limit of 1000 items in the IN clause, we handle that here
     */
    private readonly getMultivalueCondition = (fieldName: string, values: string[], includeSingleQuote = false, op = 'IN', includeUpper?: boolean, separatorCharacter?: string, columnDataType?: string) => {

        const items: string[] = [];
        const clauses: string[] = [];
        const quote = includeSingleQuote ? '\'' : '';
        const isClob = columnDataType === 'CLOB';
        const isNot = op.toUpperCase().indexOf('NOT') >= 0;
        let result = '';

        while (fieldName.indexOf('.') >= 0) {
            fieldName = fieldName.slice(fieldName.indexOf('.') + 1);
        }
        if (includeUpper) {
            fieldName = `UPPER(${fieldName})`;
        }

        for (const value of values) {
            if (value.toUpperCase() !== 'NULL') {
                if (isClob) {
                    items.push(`${(isNot ? 'NOT ' : '')}REGEXP_LIKE(${fieldName}, '(^|\\${separatorCharacter}|\\${separatorCharacter} )${value}( \\${separatorCharacter}|\\${separatorCharacter}|$)')`);
                } else {
                    items.push(`${quote}${this.replaceAnyVariables(value)}${quote}`);
                }
            }
        }
        const isNull = items.length < values.length;
        if (items.length) {
            if (isClob) {
                clauses.push(items.join(isNot ? ' AND ' : ' OR '));
            } else {
                while (items.length > 0) {
                    clauses.push(this.getCondition(fieldName, op, `(${items.splice(0, this.maxOracleItems).join(',')})`, false, false, true));
                }
            }
            result = `(${clauses.join(isNot ? ' AND ' : ' OR ')})`;
        }
        if (isNot && isNull) {
            result += (result.length ? ' AND ' : '') + this.getCondition(fieldName, 'IS NOT NULL', '');
        } else if (isNot || isNull) {
            result += (result.length ? ' OR ' : '') + this.getCondition(fieldName, 'IS NULL', '');
        }
        return `(${result})`;
    }

    /**
     * Returns a condition SQL statement given the field, operator and value
     */
    private readonly getCondition = (field: string, op: string, value: string, includeSingleQuote = false, includeUpper = false, isMultiple = false, dataType = '') => {
        if (value === 'NULL') {
            if (op === '<>' || op === 'IS NOT') {
                op = 'IS NOT NULL';
            } else {
                op = 'IS NULL';
            }
        }
        if (op.indexOf('NULL') >= 0) {
            value = null;
        }
        if (includeUpper) {
            field = `UPPER(${field})`;
        }
        if ((op === '>' || op === '>=' || op === '<' || op === '<=') && dataType === 'CLOB') {
            field = `CAST(${field} AS VARCHAR2(100))`;
        }
        const parts = [field, op];
        if (value) {
            if (includeUpper) {
                value = value.toUpperCase();
            }
            if (typeof value !== 'number') {
                value = isMultiple ? (includeSingleQuote ? `'${value}'` : value) : this.formatvalue(value, includeSingleQuote);
            }
            parts.push(value);
        }
        return parts.join(' ');
    }

    /**
     * Returns condition for comparing a date using Oracle's TRUNC and TO_DATE
     */
    private readonly getDateCondition = (field: string, op: string, value: string, multiValue?: string[]) => {
        field = field.toUpperCase();
        if (op.indexOf('NULL') < 0) {
            let format;
            if (this.hasAnyVariables(value)) {
                value = this.replaceAnyVariables(value, true);
                format = `EXTRACT(year FROM ${field}) ${op} ${value}`;
            } else {
                if (!field.length) {
                    if (value.indexOf('/') > -1) {
                        format = `TO_DATE('${value}', 'MM/DD/YYYY')`;
                    } else {
                        format = `TO_DATE('${value}', 'YYYY-MM-DD')`;
                    }
                } else {
                    value = this.replaceAnyVariables(value, true);
                    if (value.toUpperCase() === 'NULL') {
                        switch (op) {
                            case '<>':
                            case 'NOT LIKE':
                            case 'NOT IN':
                            case 'NOT INCLUDES':
                                op = 'IS NOT NULL';
                                break;
                            default:
                                op = 'IS NULL';
                                break;
                        }
                        format = `TRUNC(${field}) ${op}`;
                    } else if (op.toLowerCase().endsWith('in') && multiValue) {
                        const values = multiValue.map(v => this.getDateConversionString(v));
                        format = this.getMultivalueCondition(`TRUNC(${field})`, values, false, op);
                    } else {
                        format = `TRUNC(${field}) ${op} TO_DATE('${value}', 'YYYY-MM-DD')`;
                    }
                }
            }
            return format;
        }
        return this.getCondition(field, op, value);
    }

    private getDateConversionString(value: string) {
        let result;
        const rg1 = /^[0-9]{4}-(((0[13578]|(10|12))-(0[1-9]|[1-2][0-9]|3[0-1]))|(02-(0[1-9]|[1-2][0-9]))|((0[469]|11)-(0[1-9]|[1-2][0-9]|30)))$/g;
        const rg2 = /^[0,1]?\d{1}\/(([0-2]?\d{1})|([3][0,1]{1}))\/(([1]{1}[9]{1}[9]{1}\d{1})|([2-9]{1}\d{3}))$/g;
        if (value.match(rg1)) {
            result = `TO_DATE('${value.match(rg1)[0]}', 'YYYY-MM-DD')`;
        } else if (value.match(rg2)) {
            result = `TO_DATE('${value.match(rg2)[0]}', 'MM/DD/YYYY')`;
        } else if (value.toUpperCase() === 'NULL') {
            result = 'NULL';
        } else {
            result = `'${value}'`;
        }
        return result;
    }

    /**
     * Returns BETWEEN condition
     */
    private getBetweenCondition(columnName: string, values: string[], operator: string, includeSingleQuote = false, isDate = false, includeUpper = false, dataType: string) {
        const pair = 2;
        if (values.length < pair) {
            values.push('');
        }
        if (includeUpper) {
            columnName = `UPPER(${columnName})`;
        }
        if (dataType === 'CLOB') {
            columnName = `CAST(${columnName} AS VARCHAR2(100))`;
        }
        if (isDate) {
            includeSingleQuote = false;
            return [`TRUNC(${columnName})`, operator, this.formatvalue(this.getDateCondition('', operator, values[0]), includeSingleQuote), 'AND', this.formatvalue(values[1] !== '' ? this.getDateCondition('', operator, values[1]) : '', includeSingleQuote)].join(' ');
        }
        return [columnName, operator, this.formatvalue(values[0], includeSingleQuote), 'AND', this.formatvalue(values[1], includeSingleQuote)].join(' ');
    }

    /**
     * Returns condition for checking if the value is included in a comma separated field value
     */
    private readonly getIncludesCondition = (field: string, value: string, not = false, includeUpper = false) => {
        if (includeUpper) {
            field = `UPPER(${field})`;
        }
        let sql = `REGEXP_LIKE(${field}, '${value}')`;
        if (not) {
            sql = `NOT ${sql}`;
        }
        return sql;
    }

    /**
     * Formats the value in a condition statement
     */
    private readonly formatvalue = (value: string, includeSingleQuote: boolean) => {

        value = this.replaceAnyVariables(value);
        if (value.toUpperCase() === 'NULL') {
            return 'NULL';
        }
        return includeSingleQuote ? `'${value.trim()}'` : value;
    }

    public replaceVariables = [
        'currentYear',
        'currentFiscalYear',
        'currentFirstDayOfYear'
    ];

    public hasAnyVariables(value: string) {
        let retVal = false;
        if (value) {
            for (const variable of this.replaceVariables) {
                if (value.includes(`{${variable}`)) {
                    retVal = true;
                    break;
                }
            }
        }
        return retVal;
    }

    /**
     * Searches for variables and returns a string with the variable replacements completed
     */
    public replaceAnyVariables(value: string, includeDateDashes: boolean = false) {
        if (value && value.indexOf('{current') >= 0 && value.indexOf('}') > 0) {
            value = value.replace('{', '').replace('}', '');
            value = value.replace(/currentYear/ig, this.getCurrentYear().toString());
            value = value.replace(/currentFiscalYear/ig, this.getCurrentFiscalYear().toString());
            value = value.replace(/currentFirstDayOfYear/ig, this.getFirstDayOfYear().toString());
            if (value.indexOf('+') || value.indexOf('-')) {
                let components = value.split(/\+|\-| /);
                components = components.filter(x => x !== '');
                if (components.length > 1) {
                    if ((value.indexOf('currentYYYYMMDD') >= 0 || value.indexOf('currentYYYY-MM-DD') >= 0)) {
                        value = this.replaceCurrentDateComparisonValue(value, includeDateDashes);
                    } else {
                        const leftIndex = 0;
                        const rightIndex = components.length - 1;
                        let value1: number;
                        let value2: number;
                        value1 = parseInt(components[leftIndex], 10);
                        value2 = parseInt(components[rightIndex], 10);
                        if (value.includes('-')) {
                            value2 = value2 * -1;
                        }
                        value = (value1 + value2).toString();
                    }
                }
            }
            value = value.replace(/currentYYYYMMDD/ig, this.getYYYYMMDD());
            value = value.replace(/currentYYYY-MM-DD/ig, this.getYYYYMMDD('-'));
            if (includeDateDashes) {
                value = value.replace(/currentDate/ig, this.getYYYYMMDD('-'));
            } else {
                value = value.replace(/currentDate/ig, this.getYYYYMMDD());
            }
        }
        return value;
    }

    replaceCurrentDateComparisonValue(value: string, includeDateDashes = false): string {
        let components = value.split(/\+|\-| /);
        components = components.filter(x => x !== '');
        if ((value.indexOf('currentYYYYMMDD') >= 0 || value.indexOf('currentYYYY-MM-DD') >= 0)) {
            const rightComponent: string = components[(components.length - 1)];
            const digitsInString = rightComponent.split('').filter(x => !isNaN(parseFloat(x))).join();
            value = value.replace('currentYYYY-MM-DD', 'currentYYYYMMDD');
            if ((components.length > 1) && digitsInString.length > 0) {
                let newDate = new Date();
                let value2: number = parseInt(rightComponent, 10);
                if (value.indexOf('-') >= 0) {
                    value2 = (value2 * -1);
                }
                if ((rightComponent.indexOf('m') >= 0 || rightComponent.indexOf('months') >= 0)) {
                    newDate = this.addMonthsToDate(newDate, value2);
                } else if ((rightComponent.indexOf('d') >= 0 || rightComponent.indexOf('days') >= 0)) {
                    newDate = this.addDaysToDate(newDate, value2);
                } else {
                    newDate = this.addYearsToDate(newDate, value2);
                }
                if (includeDateDashes) {
                    value = this.getYYYYMMDD('-', newDate);
                } else {
                    value = this.getYYYYMMDD('', newDate);
                }
            } else if (includeDateDashes) {
                value = value.replace(/currentYYYYMMDD/ig, this.getYYYYMMDD('-'));
            } else {
                value = value.replace(/currentYYYYMMDD/ig, this.getYYYYMMDD());
            }
        }
        return value;
    }

    private includeUpper(query: IQuery): boolean {
        const upperOperators = ['=', '<>', 'NOT LIKE', 'LIKE', 'NOT IN', 'IN', 'INCLUDES', 'NOT INCLUDES', '>', '>=', '<', '<=', 'BETWEEN', 'NOT BETWEEN'];
        const queryDataType = this.getQueryDataType(query);
        if (query.value && upperOperators.indexOf(query.operator) >= 0
            && !this.areValuesNumeric(query)
            && this.doValuesContainAlphaChars(query)
            && (query.value.toUpperCase() !== 'NULL')) {
            if (!this.layerColumnDataTypes
                || !queryDataType
                || queryDataType === 'VARCHAR2'
                || queryDataType === 'BLOB'
                || queryDataType === 'CLOB') {
                return true;
            }
            return false;
        }
        return false;
    }

    private areValuesNumeric(query: IQuery): boolean {
        const values = (query.multiValue && query.multiValue.length) ? query.multiValue : query.value.split(',');
        return !values.some(x => x !== 'NULL' && !this.isNumeric(x));
    }

    private doValuesContainAlphaChars(query: IQuery): boolean {
        const values = query.value.split('').map((x) => x.trim());
        return values.filter((x) => this.isLetter(x)).length > 0;
    }

    private isNumeric(x: string) {
        return !isNaN(Number(x));
    }

    private isLetter(str) {
        const regex = RegExp('^[a-zA-Z()]+$');
        return regex.test(str);
    }

    public buildExternalWorkingSql(workingQuery: IWorkingQuery, layerColumns?: ILayerColumn[]): string {
        if (layerColumns && layerColumns.length > 0) {
            this.layerColumnDataTypes = layerColumns.map(x => this.getKeyValuePair(x.name, x.databaseDataType));
        }

        const joinOperator: string = workingQuery.operator;
        const subqueries: IWorkingQuery[] = workingQuery.subqueries;

        //iterate through each subquery
        //build each subquery into string format (using getCondition()?)
        //after join() all subqueries with operator

        const sqlBuilder: string[] = [];
        for (const query of subqueries) {
            const property = query.field !== null ? query.field : '';
            const operator = query.operator;
            const value = query.value !== null ? (this.isQueryFieldNumeric(query) ? query.value : `'${query.value}'`) : '';

            sqlBuilder.push([property, operator, value].join(' '));
        }
        let retVal = '';
        if (sqlBuilder.length > 1) {
            for (let i = 0; i < sqlBuilder.length; i++) {
                if (i === sqlBuilder.length - 1) {
                    if (sqlBuilder[i] !== ' = ') {
                        retVal += sqlBuilder[i];
                    } else {
                        //dont add ' = ' and remove last joinOperator
                        retVal = retVal.substring(0, retVal.lastIndexOf(joinOperator));
                    }

                } else {
                    if (sqlBuilder[i] !== ' = ') {
                        retVal += `${sqlBuilder[i]} ${joinOperator} `;
                    }
                }
            }
        } else {
            //if property and value are both blank, set to ''
            retVal = sqlBuilder[0] !== ' = ' ? sqlBuilder[0] : '';
        }
        return retVal.trim();
    }
    private getChildTableQuery(layerQuery: IQuery, query: IQuery) {
        if (layerQuery.childTableQuery && layerQuery.childTableRecord) {
            if (query === undefined) {
                query = {};
                query.operator = 'AND';
                query.subqueries = [];
            }

            query.childTableRecord = layerQuery.childTableRecord;
            const childTableQuery = this.getSimplifiedDeepCopyOfQuery(layerQuery.childTableQuery, false);
            query.childTableQuery = { subqueries: [{ field: '', operator: '=', value: null }] };
            if (childTableQuery.operator !== 'AND' && childTableQuery.operator !== 'OR') {
                if (childTableQuery.field && childTableQuery.field !== '') {
                    query.childTableQuery.subqueries = [childTableQuery];
                }
                query.childTableQuery.operator = 'AND';
            } else {
                query.childTableQuery.operator = childTableQuery.operator;
                query.childTableQuery.subqueries = [].concat(childTableQuery.subqueries);
            }
        }
        return query;
    }

    private getJoinTableQuery(layerQuery: IQuery, query: IQuery) {
        if (layerQuery.joinTableQuery && layerQuery.joinRecord) {
            if (query === undefined) {
                query = {};
                query.operator = 'AND';
                query.subqueries = [];
            }

            query.joinRecord = layerQuery.joinRecord;
            if (layerQuery.primaryLayerRMSKeys) {
                query.primaryLayerRMSKeys = layerQuery.primaryLayerRMSKeys;
            }
            const joinTableQuery = this.getSimplifiedDeepCopyOfQuery(layerQuery.joinTableQuery, false);
            query.joinTableQuery = { subqueries: [{ field: '', operator: '=', value: null }] };
            if (joinTableQuery.operator !== 'OR' && joinTableQuery.operator !== 'AND') {
                if (joinTableQuery.field && joinTableQuery.field !== '') {
                    query.joinTableQuery.subqueries = [joinTableQuery];
                }
                query.joinTableQuery.operator = 'AND';
            } else {
                query.joinTableQuery.operator = joinTableQuery.operator;
                query.joinTableQuery.subqueries = [].concat(joinTableQuery.subqueries);
            }
        }
        return query;
    }
}

export interface IColumnDataType {
    key: string;
    value: string;
}
